import { ChartNoAxesCombined, ShieldPlus, Award, HeartHandshake, Check, ShieldCheck } from "lucide-react";

const REASONS = [
  {
    icon: ChartNoAxesCombined,
    title: "Największe doświadczenie w Europie",
    desc: "Hiszpania wykonuje rocznie najwięcej cykli zapłodnienia pozaustrojowego na kontynencie. Ta skala przekłada się na ogromne doświadczenie lekarzy i embriologów oraz najwyższe wskaźniki skuteczności, sięgające nawet 75% w zaawansowanych programach.",
  },
  {
    icon: ShieldPlus,
    title: "Bezpieczeństwo oparte na prawie",
    desc: "Hiszpańskie prawo (m.in. ustawa Ley 14/2006) jest uznawane za jedno z najbardziej przejrzystych i liberalnych, a jednocześnie surowo regulujących standardy kliniczne.",
    subItems: [
      {
        label: "Dostępność",
        text: "Z leczenia mogą korzystać pary, kobiety samotne oraz pary jednopłciowe.",
      },
      {
        label: "Gwarancja jakości",
        text: "Każda klinika podlega rygorystycznym kontrolom państwowym oraz rejestrowi SEF (Hiszpańskiego Towarzystwa Płodności), co zapewnia pełną transparentność wyników.",
      },
    ],
  },
  {
    icon: Award,
    title: "Lider innowacji i badań genetycznych",
    desc: "To właśnie w Hiszpanii narodziło się wiele metod, które dziś są standardem światowym. Dzięki ścisłej współpracy z hiszpańskimi laboratoriami, w ramach rozwiązań takich jak Secure Match czy Secure Natal, oferujemy:",
    subItems: [
      {
        label: "Największą bazę dawców",
        text: "Co oznacza brak kolejek i możliwość precyzyjnego dopasowania genetycznego.",
      },
      {
        label: "Zaawansowane PGT (Badania Preimplantacyjne)",
        text: "Hiszpańskie procedury pozwalają na najdokładniejsze sprawdzenie zdrowia zarodka przed transferem.",
      },
    ],
  },
  {
    icon: HeartHandshake,
    title: "Holistyczna opieka i etyka",
    desc: "Hiszpański rynek medyczny stawia pacjenta w centrum. Standardem jest nie tylko skuteczność medyczna, ale także opieka psychologiczna i pełne bezpieczeństwo prawne rodziców oraz dzieci.",
  },
];

export default function WhySpain() {
  return (
    <section
      id="why-spain-section"
      data-testid="why-spain-section"
      className="relative py-24 lg:py-32 bg-nuuma-primary text-white overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute top-0 right-0 h-[500px] w-[500px] rounded-full opacity-20 blur-3xl"
        style={{
          background:
            "radial-gradient(circle, rgba(238,159,91,0.8) 0%, transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="absolute bottom-0 left-0 h-[400px] w-[400px] rounded-full opacity-15 blur-3xl"
        style={{
          background:
            "radial-gradient(circle, rgba(239,96,121,0.8) 0%, transparent 70%)",
        }}
      />

      <div className="nuuma-container relative z-10">
        <div className="mb-16">
          <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-nuuma-accent1 mb-3">
            Trust Signal
          </span>
          <h2 className="max-w-4xl font-display font-bold text-white text-3xl sm:text-4xl lg:text-[2.6rem] leading-[1.15]">
            Dlaczego Hiszpania? Europejskie{" "}
            <span className="text-nuuma-accent1">Centrum Doskonałości IVF</span>
          </h2>
          <div className="mt-6 space-y-4 text-white/80 text-base lg:text-lg leading-relaxed">
            <p>
              Wybór Hiszpanii jako miejsca diagnostyki i leczenia to decyzja o
              opiece w najlepiej uregulowanym i najbardziej zaawansowanym
              ekosystemie medycznym w Europie. Wiele z polskich klinik także
              wysyła próbki do Hiszpanii, ponieważ to zapewnia najwyższy standard.
            </p>
            <p>
              Nuuma Genetics oferuje polskim pacjentom bezpośredni dostęp do
              hiszpańskiej jakości, a także innowacji.
            </p>
          </div>
        </div>

        {/* Mobile Layout (Original) */}
        <div className="grid md:hidden grid-cols-1 gap-6">
          {REASONS.map((r, i) => (
            <ReasonCard key={i} r={r} i={i} />
          ))}
        </div>

        {/* Desktop Masonry Layout */}
        <div className="hidden md:flex gap-6 lg:gap-8 items-start">
          {/* Left Column: Card 1 (short) & Card 3 (tall) */}
          <div className="flex-1 flex flex-col gap-6 lg:gap-8">
            <ReasonCard r={REASONS[0]} i={0} className="h-fit" />
            <ReasonCard r={REASONS[2]} i={2} className="h-full min-h-[380px]" />
          </div>
          {/* Right Column: Card 2 (tall) & Card 4 (short) */}
          <div className="flex-1 flex flex-col gap-6 lg:gap-8">
            <ReasonCard r={REASONS[1]} i={1} className="h-full min-h-[380px]" />
            <ReasonCard r={REASONS[3]} i={3} className="h-fit" />
          </div>
        </div>

        {/* Outro Block */}
        <div className="mt-16 lg:mt-24 w-full bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-nuuma-l p-6 lg:p-10 flex flex-col md:flex-row items-center gap-6 lg:gap-10 hover:bg-white/[0.06] hover:border-white/20 transition-all duration-300">
          <div className="flex items-center gap-6 flex-1 text-left">
            <div className="h-14 w-14 lg:h-16 lg:w-16 shrink-0 rounded-2xl bg-nuuma-accent1/15 flex items-center justify-center">
              <ShieldCheck className="h-7 w-7 lg:h-8 lg:w-8 text-nuuma-accent1" strokeWidth={2} />
            </div>
            <p className="text-[0.95rem] lg:text-[1.05rem] text-white/80 leading-relaxed font-light">
              <strong className="font-semibold text-white">Wybierając Nuuma Genetics,</strong> zyskujesz dostęp do najwyższego standardu medycznego. Bez barier językowych i z pełnym wsparciem w Polsce.
            </p>
          </div>
          <div className="shrink-0 w-full md:w-auto">
            <a
              href="#kontakt"
              className="inline-flex items-center justify-center w-full md:w-auto px-7 py-3 rounded-full border border-nuuma-accent1 text-nuuma-accent1 font-medium text-sm hover:bg-nuuma-accent1/10 transition-colors"
            >
              Umów konsultację
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function ReasonCard({ r, i, className = "h-full" }) {
  const Icon = r.icon;
  return (
    <div
      data-testid={`why-spain-card-${i}`}
      className={`group relative p-8 lg:p-10 rounded-nuuma-l bg-white/[0.04] border border-white/10 backdrop-blur-sm hover:bg-white/[0.06] hover:border-white/20 transition-all duration-300 flex flex-col ${className}`}
    >
      <div className="h-14 w-14 rounded-2xl bg-nuuma-accent1/15 flex items-center justify-center mb-6 shrink-0">
        <Icon className="h-7 w-7 text-nuuma-accent1" />
      </div>
      <h4 className="font-display font-bold text-nuuma-accent1 text-xl lg:text-2xl mb-4">
        {i + 1}. {r.title}
      </h4>
      <p className="text-[0.95rem] lg:text-base text-white/80 leading-relaxed mb-6">
        {r.desc}
      </p>

      {r.subItems && r.subItems.length > 0 && (
        <ul className="space-y-5 mt-auto pt-2 border-t border-white/10">
          {r.subItems.map((sub, j) => (
            <li key={j} className="flex gap-3 items-start">
              <span className="shrink-0 mt-1 h-5 w-5 rounded-full bg-nuuma-accent1/20 flex items-center justify-center">
                <Check className="h-3 w-3 text-nuuma-accent1" strokeWidth={3} />
              </span>
              <span className="text-[0.9rem] lg:text-[0.95rem] leading-relaxed text-white/70">
                <strong className="text-white block mb-0.5">{sub.label}</strong>
                {sub.text}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
